import type { PlotState } from '../../state/plot-state.ts';
import type { NumberAgg } from '../../api/types.ts';

export const NUMBER_AGGS: Array<{ value: NumberAgg; label: string }> = [
  { value: 'latest', label: 'Latest value' },
  { value: 'avg', label: 'Average' },
  { value: 'sum', label: 'Sum' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
  { value: 'count', label: 'Count of numeric values' },
];

/** Keep aggregate arguments in sync when a derived signal is renamed or removed. */
export function rewriteNumberSignal(expression: string, name: string, replacement: string | null): string {
  let removed = false;
  // Standalone quoted strings are consumed first so text inside a signal name
  // cannot be mistaken for another aggregate call.
  const tokens = /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\b(latest|avg|sum|min|max|count)\s*\(\s*("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|[A-Za-z_]\w*)\s*\)/gi;
  const result = expression.replace(tokens, (match: string, agg: string | undefined, arg: string | undefined) => {
    if (!agg || !arg) return match;
    let column = arg;
    if (arg.startsWith('"')) {
      try { column = JSON.parse(arg) as string; } catch { return match; }
    } else if (arg.startsWith("'")) {
      column = arg.slice(1, -1);
    }
    if (column !== name) return match;
    if (replacement === null) { removed = true; return match; }
    return `${agg}(${JSON.stringify(replacement)})`;
  });
  return removed ? '' : result;
}

export function serializeNumber(s: PlotState, params: URLSearchParams): void {
  if (s.number_mode === 'formula') {
    params.set('nmode', 'formula');
    if (s.number_expression) params.set('nexpr', s.number_expression);
  } else {
    if (s.number_column) params.set('ncol', s.number_column);
    if (s.number_agg !== 'latest') params.set('nagg', s.number_agg);
  }
  if (s.number_label) params.set('nlabel', s.number_label);
  if (s.number_unit) params.set('nunit', s.number_unit);
  if (s.number_decimals !== 2) params.set('ndp', String(s.number_decimals));
}

export function parseNumber(params: URLSearchParams, s: PlotState): void {
  if (params.get('nmode') === 'formula') s.number_mode = 'formula';
  s.number_column = params.get('ncol') ?? s.number_column;
  const agg = params.get('nagg');
  if (NUMBER_AGGS.some((a) => a.value === agg)) s.number_agg = agg as NumberAgg;
  s.number_expression = params.get('nexpr') ?? s.number_expression;
  s.number_label = (params.get('nlabel') ?? s.number_label).slice(0, 120);
  s.number_unit = (params.get('nunit') ?? s.number_unit).slice(0, 40);
  const dp = params.get('ndp');
  if (dp !== null && Number.isFinite(Number(dp))) s.number_decimals = Math.max(0, Math.min(10, Math.round(Number(dp))));
}
